import { Amount, getChecksumAddressOrThrow, Usd } from '@silo/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ContractTransaction } from 'ethers';
import { claimRewards } from '../silo/rewards/claimRewards';
import { GLOBAL_CONFIG } from '@silo/config';
import { useSiloWeb3 } from '@silo/web3';
import { isNullable } from 'effect/Predicate';
import { revenueDataKey } from 'app/queries/use-query-revenue-data';
import useNotify from 'app/hooks/useNotify';
import { BalanceDisplay, ClaimSuccess } from '../silo/rewards/components';
import { Button, Dots } from '@silo/ui';

const {
  siloIncentivesControllerAddress,
  shareTokenAddress,
  protocolKey,
  rewardTokenAddress,
  rewardTokenDecimals,
  rewardTokenSymbol,
} = GLOBAL_CONFIG.revenue;

interface ClaimRevenueFormProps {
  revenueBalance: Amount;
  revenueBalanceUsd: Usd;
}

export function ClaimRevenueForm({ revenueBalance, revenueBalanceUsd }: ClaimRevenueFormProps): JSX.Element {
  const { clientProvider, signingProvider, account } = useSiloWeb3();
  const queryClient = useQueryClient();
  const { notifyError } = useNotify();
  const shareTokenAddresses = [getChecksumAddressOrThrow(shareTokenAddress)];

  const {
    isLoading: isPending,
    mutate,
    isSuccess,
    data,
  } = useMutation<ContractTransaction, Error>({
    mutationFn: async () => {
      if (isNullable(clientProvider) || isNullable(signingProvider) || isNullable(account)) {
        throw new Error('claimRewards params undefined');
      }

      return claimRewards({
        siloIncentivesControllerAddress: getChecksumAddressOrThrow(siloIncentivesControllerAddress),
        signingProvider,
        clientProvider,
        account,
        shareTokenAddresses,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([revenueDataKey]);
    },
    onError: (error) => {
      notifyError(error.message);
    },
  });

  if (isSuccess) {
    return <ClaimSuccess data={data} />;
  }

  return (
    <div className="flex flex-col gap-8 sm:gap-6">
      <h2 className="text-xl font-medium">Claim your revenue</h2>

      <BalanceDisplay
        protocolKey={protocolKey}
        rewardTokenAddress={getChecksumAddressOrThrow(rewardTokenAddress)}
        rewardTokenDecimals={rewardTokenDecimals}
        rewardTokenSymbol={rewardTokenSymbol}
        balance={revenueBalance}
        balanceUsd={revenueBalanceUsd}
      />

      <div>
        <Button variant="primary" disabled={isPending} className="w-full md:w-fit" onClick={() => mutate()}>
          {isPending ? <Dots>Pending</Dots> : 'Claim'}
        </Button>
      </div>
    </div>
  );
}
